import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Calendar,
  Filter,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
  Layers,
  Sparkles,
  BarChart3,
  ShieldCheck,
  Award
} from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { generateDashboardPdfReport } from '../../../shared/utils/dashboardPdfGenerator.js';

interface ExportPdfModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type DatePreset = 'all' | 'today' | '7days' | '30days' | 'this_month' | 'this_year' | 'custom';

export const ExportPdfModal: React.FC<ExportPdfModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuthStore();

  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Fetch Stats
  const { data: stats } = useQuery({
    queryKey: ['stats', user?.id, user?.role],
    queryFn: () => api.getDashboardStats(),
    enabled: isOpen,
  });

  // Fetch books list for inventory sample in PDF
  const { data: booksData, isLoading: isLoadingBooks } = useQuery({
    queryKey: ['books', 'for-pdf-export', user?.id],
    queryFn: () => api.getBooks(),
    enabled: isOpen,
  });

  const allBooks = booksData?.books || [];

  // Date Presets Handler
  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    setErrorMsg(null);

    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);

    if (preset === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (preset === 'today') {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (preset === '7days') {
      const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(todayStr);
    } else if (preset === '30days') {
      const past = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(todayStr);
    } else if (preset === 'this_month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(firstDay.toISOString().slice(0, 10));
      setEndDate(todayStr);
    } else if (preset === 'this_year') {
      const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
      setStartDate(firstDayOfYear.toISOString().slice(0, 10));
      setEndDate(todayStr);
    }
  };

  // Filtered Books calculation
  const matchingBooks = useMemo(() => {
    return allBooks.filter((b) => {
      // Date filter
      const bookDate = (b.createdAt || '').slice(0, 10);
      if (startDate && bookDate < startDate) return false;
      if (endDate && bookDate > endDate) return false;

      // Status filter
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;

      // Category filter
      if (categoryFilter !== 'all' && b.category !== categoryFilter) return false;

      return true;
    });
  }, [allBooks, startDate, endDate, statusFilter, categoryFilter]);

  // Categories list
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    allBooks.forEach((b) => {
      if (b.category) set.add(b.category);
    });
    return Array.from(set).sort();
  }, [allBooks]);

  const handleExportPdf = async () => {
    if (!stats) {
      setErrorMsg('Data statistik belum tersedia. Harap tunggu sebentar.');
      return;
    }

    setIsExporting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      let label = 'Seluruh Katalog (Semua Waktu)';
      if (startDate && endDate) {
        label = `${startDate} s/d ${endDate}`;
      } else if (startDate) {
        label = `Mulai ${startDate}`;
      } else if (endDate) {
        label = `Sampai ${endDate}`;
      }

      await generateDashboardPdfReport({
        stats: stats,
        user: user,
        filteredBooks: matchingBooks,
        dateRangeLabel: label,
      });

      setSuccessMsg('Laporan PDF Inventaris & Pemindaian UIBN berhasil diterbitkan dan diunduh!');
      setTimeout(() => {
        setSuccessMsg(null);
      }, 5000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Gagal membuat berkas Laporan PDF.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto animate-in fade-in duration-150">
      <div className="relative w-full max-w-xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden my-auto flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="p-4 sm:p-5 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center shrink-0 shadow-inner">
              <FileText className="w-5 h-5 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold tracking-tight text-white flex items-center gap-2">
                <span>Cetak Laporan PDF Resmi UIBN</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/30">
                  PDF A4
                </span>
              </h2>
              <p className="text-xs text-indigo-200/80">
                Ekspor rekapitulasi statistik inventaris buku, tren pemindaian, dan matriks kesiapan publikasi.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 text-indigo-200 hover:text-white hover:bg-white/10 rounded-xl transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body (Scrollable) */}
        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1 text-xs">
          {/* Quick Date Presets */}
          <div>
            <label className="block text-xs font-bold text-slate-800 mb-2 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-indigo-600" />
              <span>Opsi Rentang Waktu Laporan</span>
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2">
              {[
                { id: 'all', label: 'Semua Waktu' },
                { id: 'today', label: 'Hari Ini' },
                { id: '7days', label: '7 Hari Terakhir' },
                { id: '30days', label: '30 Hari Terakhir' },
                { id: 'this_month', label: 'Bulan Ini' },
                { id: 'this_year', label: 'Tahun Ini' },
              ].map((p, pIdx) => (
                <button
                  key={`pdf-preset-${p.id}-${pIdx}`}
                  type="button"
                  onClick={() => handlePresetChange(p.id as DatePreset)}
                  className={`px-2.5 py-2 rounded-xl text-xs font-semibold border transition cursor-pointer min-h-[38px] ${
                    datePreset === p.id
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Custom Inputs */}
          <div className="p-3 sm:p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
            <span className="text-[11px] font-bold text-slate-700 block">Kustomisasi Tanggal Pendaftaran</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-slate-500 mb-1">Mulai Dari</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 font-mono outline-hidden min-h-[38px]"
                />
              </div>

              <div>
                <label className="block text-[10px] text-slate-500 mb-1">Sampai Dengan</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 font-mono outline-hidden min-h-[38px]"
                />
              </div>
            </div>
          </div>

          {/* Filters for Sample Inventory */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-indigo-600" />
                <span>Filter Status Naskah</span>
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-hidden min-h-[38px]"
              >
                <option value="all">Semua Status (Seluruh Naskah)</option>
                <option value="disetujui">Hanya Disetujui (Resmi)</option>
                <option value="diajukan">Hanya Diajukan (Review)</option>
                <option value="draft">Hanya Draft</option>
                <option value="diarsipkan">Hanya Diarsipkan</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-indigo-600" />
                <span>Filter Kategori</span>
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-hidden min-h-[38px]"
              >
                <option value="all">Semua Kategori</option>
                {categoryOptions.map((cat, idx) => (
                  <option key={`${cat}-${idx}`} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Report Preview Highlights */}
          <div className="p-3.5 rounded-2xl bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 border border-indigo-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-indigo-700" />
                <span className="font-bold text-slate-900">Cakupan Berkas Laporan PDF</span>
              </div>
              <span className="px-2 py-0.5 rounded-full bg-indigo-600 text-white font-bold text-[10px]">
                Dokumen Otentik
              </span>
            </div>

            <p className="text-[11px] text-slate-600 leading-relaxed">
              Berkas PDF berisi: Metrik Ringkasan (Total Buku, Pemindaian, Status), Grafik Tren Aktivitas 7 Hari,
              Daftar Buku Terpopuler, dan Sampel Inventaris Naskah Buku ({matchingBooks.length} judul tersaring).
            </p>
          </div>

          {/* Feedback Messages */}
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-3.5 sm:p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-3 shrink-0">
          <span className="text-[11px] text-slate-500">
            Termasuk stempel validasi & ttd kriptografis.
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold transition cursor-pointer min-h-[40px]"
            >
              Batal
            </button>

            <button
              type="button"
              onClick={handleExportPdf}
              disabled={isExporting}
              className="px-4 sm:px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold transition flex items-center justify-center gap-2 shadow-sm cursor-pointer disabled:opacity-50 min-h-[40px]"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Menerbitkan PDF...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Unduh Laporan PDF</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
